import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import SvgIcon from 'components/atoms/SvgIcon'
import * as constants from '../../../static/constants'
import Button from '../../atoms/Button'

interface IProps {
  guideReport: any
}

const CONTENT_HEIGHT = 32

const downloadFile = async (url: string, filename: string) => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`ダウンロード失敗: ${response.status} ${response.statusText}`)
    }
    const blob = await response.blob()
    const objectUrl = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(objectUrl)
  } catch (error) {
    console.error('ファイルのダウンロードに失敗しました', error)
  }
}

const buildImageFilename = (guideReport: any, fileNumber: number): string => {
  const guideName = guideReport.guide?.username ?? 'unknown'
  const tourShortName = guideReport.post?.short_name ?? 'unknown'
  const date = guideReport.tour?.day ?? 'unknown'
  return `${[guideName, tourShortName, date, fileNumber].join('_')}.jpg`
}

const buildExpenseFilename = (guideReport: any, fileNumber: number): string => {
  const guideName = guideReport.guide?.username ?? 'unknown'
  const tourShortName = guideReport.post?.short_name ?? 'unknown'
  const date = guideReport.tour?.day ?? 'unknown'
  return `${[guideName, tourShortName, date, 'expense', fileNumber].join('_')}.jpg`
}

const ReportBody: React.FC<IProps> = ({ guideReport }) => {
  const bodyRef = React.useRef(null)
  const [hasMoreContent, setHasMoreContent] = React.useState(false)
  const [showMore, setShowMore] = React.useState(false)

  React.useEffect(() => {
    if (bodyRef.current) {
      const pxHeight = bodyRef.current.offsetHeight
      const remInPx = parseFloat(getComputedStyle(document.documentElement).fontSize)
      const heightInRem = pxHeight / remInPx
      setHasMoreContent(heightInRem >= CONTENT_HEIGHT)
    }
  }, [])

  return (
    <Wrapper showMore={showMore} hasMoreContent={hasMoreContent} contentHeight={CONTENT_HEIGHT}>
      <div className="guideReportBody" ref={bodyRef}>
        <div className="reportDetail">
          <div className="numberAlert">
            {/* 予約人数と違うときだけ表示する(特殊ガイドはtour_groupがないため比較しない) */}
            {guideReport.tour_group &&
              guideReport.num_of_adults !== guideReport.tour_group.num_of_adults && (
                <div className="adultsAlert">
                  <SvgIcon name="warning" className="icon" ariaHidden={true} role="presentation" />
                  <span>{I18n.t('guide_report.num_of_adults')}:</span>
                  {guideReport.num_of_adults}
                </div>
              )}
            {guideReport.tour_group &&
              guideReport.num_of_children !== guideReport.tour_group.num_of_children && (
                <div className="childrenAlert">
                  <SvgIcon name="warning" className="icon" ariaHidden={true} role="presentation" />
                  <span>{I18n.t('guide_report.num_of_children')}: </span>
                  {guideReport.num_of_children}
                </div>
              )}
            {guideReport.tour_group &&
              guideReport.num_of_infants !== guideReport.tour_group.num_of_infants && (
                <div className="infantsAlert">
                  <SvgIcon name="warning" className="icon" ariaHidden={true} role="presentation" />
                  <span>{I18n.t('guide_report.num_of_infants')}:</span>
                  {guideReport.num_of_infants}
                </div>
              )}
          </div>
          {guideReport.number_change_reason && (
            <div className="reason">
              <span>{I18n.t('guide_report.number_change_reason')}: </span>
              {guideReport.number_change_reason}
            </div>
          )}
          {guideReport.report && (
            <div className="report">
              <span>{I18n.t('guide_report.report')}:</span>
              {guideReport.report}
            </div>
          )}
          {guideReport.problem && (
            <div className="problem">
              <span>{I18n.t('guide_report.problem')}:</span>
              {guideReport.problem}
            </div>
          )}
          {guideReport.expenses.length > 0 && (
            <div className="expenses">
              <span className="label">{I18n.t('guide_report_expense.expense')}:</span>
              <div className="expenseList">
                {guideReport.expenses.map((expense, index) => (
                  <div className="expense" key={expense.id}>
                    {expense.image_url && (
                      <div className="expenseImage">
                        {expense.content_type === 'application/pdf' ? (
                          <p>{expense.filename}</p>
                        ) : (
                          <img
                            src={expense.image_url}
                            alt={expense.name ?? ''}
                            onClick={() =>
                              window.open(expense.image_url, '_blank', 'noopener,noreferrer')
                            }
                            title="クリックで費用を別タブに表示"
                          />
                        )}
                      </div>
                    )}
                    <div className="expenseInfo">
                      <div className="name">{expense.name}</div>
                      <div className="number">￥ {expense.expense}</div>
                    </div>
                    {expense.image_url && (
                      <Button
                        small={true}
                        handleClick={() =>
                          downloadFile(
                            expense.image_url,
                            buildExpenseFilename(guideReport, index + 1)
                          )
                        }
                      >
                        {I18n.t('generic.download')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {guideReport.images.length > 0 && (
            <div className="images">
              <span className="label">{I18n.t('generic.photo')}:</span>
              <div className="imageList">
                {guideReport.images.map((image, index) => (
                  <div className="imageContainer" key={image.id}>
                    {(image.large_url || image.image_url) && (
                      <img
                        src={image.large_url || image.image_url}
                        alt={image.description ?? ''}
                        onClick={() =>
                          window.open(
                            image.large_url || image.image_url,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }
                        title="クリックで写真を別タブに表示"
                      />
                    )}
                    <div className="description">{image.description}</div>
                    {(image.large_url || image.image_url) && (
                      <Button
                        small={true}
                        handleClick={() =>
                          downloadFile(
                            image.large_url || image.image_url,
                            buildImageFilename(guideReport, index + 1)
                          )
                        }
                      >
                        {I18n.t('generic.download')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {hasMoreContent && (
        <div className="showMore" onClick={() => setShowMore(!showMore)}>
          {showMore ? I18n.t('generic.show_less') : I18n.t('generic.show_more')}
        </div>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  .guideReportBody {
    white-space: pre-line;
    max-height: ${({ showMore, contentHeight }) => (showMore ? '100%' : `${contentHeight}rem`)};
    overflow: hidden;

    ${({ hasMoreContent, showMore }) =>
      hasMoreContent &&
      !showMore &&
      `
        mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
        -webkit-mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
      `}
  }

  .reportDetail {
    font-size: 1.3rem;
    color: ${constants.COLORS.black};
    word-break: break-all;
    > div {
      border-top: dashed 1px ${constants.COLORS.divider2};
    }
    .numberAlert {
      display: flex;
      align-items: center;
      border-top: 0;
      .adultsAlert,
      .childrenAlert,
      .infantsAlert {
        margin: 0 1.2rem 1.2rem 0;
        color: ${constants.COLORS.caution};
        font-size: 1.4rem;
        font-weight: bold;
        border: 1px solid ${constants.COLORS.caution};
        border-radius: 0.4rem;
        padding: 0.4rem 0.8rem;
        display: flex;
        align-items: center;
        .icon {
          width: 1.6rem;
          height: 1.6rem;
          margin-right: 0.4rem;
          fill: ${constants.COLORS.caution};
        }
        span {
          color: ${constants.COLORS.black};
          margin-right: 0.4rem;
        }
      }
    }
    .reason,
    .report,
    .problem {
      display: flex;
      padding-top: 0.8rem;
      margin-bottom: 0.8rem;
      span {
        flex: 0 0 auto;
        width: 10rem;
        color: ${constants.COLORS.subText};
        font-weight: bold;
      }
    }
    .expenses {
      display: flex;
      padding-top: 0.8rem;
      margin-bottom: 1.2rem;

      .label {
        flex: 0 0 auto;
        width: 10rem;
        margin-bottom: 0.8rem;
        color: ${constants.COLORS.subText};
        font-weight: bold;
      }
      .expenseList {
        display: flex;
        gap: 1.2rem;
        flex-wrap: wrap;
      }
      .expense {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.8rem;
        padding: 0.8rem;
        width: 16rem;
        border: solid 1px ${constants.COLORS.divider2};
        border-radius: 0.4rem;
        .expenseImage {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 8rem;
          height: 8rem;
          background-color: ${constants.COLORS.lightGray};
          border-radius: 0.4rem;
          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            cursor: pointer;
          }
          p {
            font-size: 1.1rem;
            word-break: break-all;
            padding: 0.4rem;
          }
        }
        .expenseInfo {
          width: 100%;
        }
        .name {
          font-size: 0.9rem;
          word-break: break-all;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
        }
        .number {
          color: ${constants.COLORS.primary};
          font-weight: bold;
        }
        .Button {
          width: 100%;
          margin-top: auto;
        }
      }
    }
    .images {
      display: flex;
      padding-top: 0.8rem;
      margin-bottom: 1.2rem;

      .label {
        flex: 0 0 auto;
        width: 10rem;
        margin-bottom: 0.8rem;
        color: ${constants.COLORS.subText};
        font-weight: bold;
      }
      .imageList {
        display: flex;
        gap: 1.2rem;
        flex-wrap: wrap;
      }
      .imageContainer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.8rem;
        padding: 0.8rem;
        width: 16rem;
        border: solid 1px ${constants.COLORS.divider2};
        border-radius: 0.4rem;
        img {
          width: 8rem;
          height: 8rem;
          object-fit: contain;
          background-color: ${constants.COLORS.lightGray};
          border-radius: 0.4rem;
          cursor: pointer;
        }
        .description {
          font-size: 0.9rem;
          word-break: break-all;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }
        .Button {
          width: 100%;
          margin-top: auto;
        }
      }
    }
  }
  .showMore {
    color: ${constants.COLORS.primary};
    text-decoration: underline;
    cursor: pointer;
    font-size: 1.4rem;
    &:hover {
      text-decoration: none;
    }
  }
`

export default ReportBody
